// Notes view — member notes and AI-generated notes with filtering and creation.
// migrated 2026-04-27 to SDK route workspaceProject.documents.notes

import { _navigate } from './Main.js'
import { getSDK } from '../../../shared/functions/sdk.js'

const _filteredNotes = (s) => {
  const filter = s.root.notesFilter || 'all'
  const q = (s.root.query || '').toLowerCase().trim()
  let notes = s.root.notes || []
  if (filter !== 'all') notes = notes.filter((n) => n.source === filter)
  if (q) {
    notes = notes.filter(
      (n) =>
        (n.title || '').toLowerCase().includes(q) ||
        (n.content || '').toLowerCase().includes(q) ||
        (n.author || '').toLowerCase().includes(q) ||
        (n.tags || []).some((t) => t.toLowerCase().includes(q))
    )
  }
  return notes
}

export const NotesMain = {
  extends: 'AppMain',
  padding: '0',
  Header: { extends: 'NotesHeader' },
  Body: {
    padding: 'B C',
    overflow: 'auto',
    flow: 'y',
    gap: 'A',

    NotesList: {}
  }
}

export const NotesHeader = {
  extends: 'DocsContentHeader',

  LeftGroup: {
    Title: { text: 'Notes' },
    Count: {
      text: (el, s) => {
        const n = _filteredNotes(s).length
        const filter = s.root.notesFilter || 'all'
        const label =
          filter === 'ai'
            ? 'AI note'
            : filter === 'member'
              ? 'member note'
              : 'note'
        return `${n} ${label}${n === 1 ? '' : 's'}`
      }
    }
  },

  RightGroup: {
    Search: {
      placeholder: 'Search notes...',
      value: (el, s) => s.root.query || '',
      onInput: (e, el, s) => {
        s.root.update({ query: e.target.value })
      }
    },

    PrimaryBtn: {
      text: '+ New note',
      onClick: async (e, el, s) => {
        const row = {
          title: 'Untitled note',
          content: '',
          author: s.root.currentUserEmail || 'Unknown',
          source: 'member',
          tags: []
        }
        let data
        try {
          // migrated 2026-04-27 to SDK route workspaceProject.documents.notes
          const result = await getSDK().execute('workspaceProject.documents.notes', 'create', { payload: row })
          data = result?.data || result
        } catch (err) {
          console.error('[Notes] Insert failed:', err)
          return
        }
        if (!data?.id) return
        const notes = s.root.notes || []
        s.root.update({
          notes: [
            ...notes,
            {
              id: data.id,
              ...row,
              createdAt: new Date().toISOString().slice(0, 10)
            }
          ],
          selectedNoteId: data.id
        })
      }
    }
  }
}

export const NotesList = {
  flow: 'y',
  gap: 'Z1',

  childExtends: 'NoteCard',
  childrenAs: 'state',
  children: (el, s) => _filteredNotes(s)
}

export const NoteCard = {
  extends: 'Link',
  href: (el, s) => `/documents/editor#note=${encodeURIComponent(s.id || '')}`,
  textDecoration: 'none',
  color: 'inherit',
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'A',
  borderWidth: '1px',
  borderColor: (el, s) => (s.root.selectedNoteId === s.id ? 'title' : 'line'),
  borderStyle: 'solid',
  background: 'overlayStrong',
  cursor: 'pointer',
  transition: 'A defaultBezier',
  ':hover': { borderColor: 'title' },
  onClick: (e, el, s) => {
    e.preventDefault()
    const content =
      s.content && typeof s.content === 'object' ? s.content : { blocks: [] }
    s.root.update({
      selectedNoteId: s.id,
      editorDoc: {
        id: s.id,
        title: s.title || 'Untitled note',
        content,
        source: s.source || 'member',
        author: s.author
      },
      editorMode: 'view',
      activeSection: 'editor'
    })
    _navigate(el, `/documents/editor#note=${encodeURIComponent(s.id || '')}`)
  },

  Top: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z1',

    TitleGroup: {
      flow: 'x',
      align: 'center left',
      gap: 'Z1',
      flex: '1',
      minWidth: '0',

      Icon: {
        icon: (el, s) => (s.source === 'ai' ? 'star' : 'edit'),
        boxSize: 'A1 A1',
        color: (el, s) => (s.source === 'ai' ? 'cyan' : 'caption')
      },
      Title: {
        tag: 'h3',
        margin: '0',
        fontSize: 'A',
        fontWeight: '600',
        color: 'title',
        flex: '1',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.title || 'Untitled'
      }
    },

    SourceBadge: {
      tag: 'span',
      fontSize: 'Y1',
      fontWeight: '600',
      padding: 'V Z',
      round: 'Y',
      background: (el, s) => (s.source === 'ai' ? 'cyan' : 'overlay'),
      color: (el, s) => (s.source === 'ai' ? 'white' : 'caption'),
      textTransform: 'uppercase',
      letterSpacing: '.05em',
      text: (el, s) => (s.source === 'ai' ? 'AI' : 'Member')
    }
  },

  Content: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    color: 'caption',
    lineHeight: '1.5',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => s.content || 'Empty note'
  },

  Bottom: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    marginTop: 'Z',

    Tags: {
      flow: 'x',
      gap: 'V',
      childExtends: 'NoteTag',
      childrenAs: 'state',
      children: (el, s) => (s.tags || []).map((t) => ({ label: t }))
    },

    Meta: {
      flow: 'x',
      gap: 'Z',
      fontSize: 'Y2',
      color: 'caption',
      Author: { tag: 'span', text: (el, s) => s.author || '' },
      Date: { tag: 'span', text: (el, s) => s.createdAt || '' }
    }
  }
}

export const NoteTag = {
  tag: 'span',
  fontSize: 'Y1',
  padding: 'V Z',
  round: 'Y',
  background: 'overlay',
  color: 'caption',
  text: (el, s) => s.label || ''
}
